import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import pdfIcon from 'assets/images/icons/pdf-page-grey.svg';
import plusIcon from 'assets/images/icons/plus.svg';
import sendIcon from 'assets/images/icons/send.svg';
import messageSquareGreyIcon from 'assets/images/icons/message-square-grey.svg';
import crossIcon from 'assets/images/icons/cross-grey.svg';
import imageIcon from 'assets/images/icons/image.svg';
import userCircleGreenIcon from 'assets/images/icons/users-circle-green.svg';
import userGreyIcon from 'assets/images/icons/user-grey.svg';
import pdfGreenIcon from 'assets/images/icons/pdf-page.svg';
import trashIcon from 'assets/images/icons/trash-grey.svg';
import AlertModal from 'components/common-components/AlertModal';
import Loader from 'components/common-components/Loader';
import NestedSelect from 'components/form-components/NestedSelect';
import { CallAPI } from 'actions/General';
import { uploadFile } from 'pages/company/utils';

const AddCommentModal = ({ commentsInfo, closeModal, currentUserId, isNestedModal }) => {
  const { t } = useTranslation();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const urlsRef = useRef({});

  const addCommentPayload = {
    isTestSubmission: commentsInfo.isTestSubmission,
    submissionId: commentsInfo.submissionId,
    ...(commentsInfo.elementGlobalId && { elementGlobalId: commentsInfo.elementGlobalId }),
    ...(commentsInfo.sectionRepetitionNumber && {
      sectionRepetitionNumber: commentsInfo.sectionRepetitionNumber,
    }),
    form: commentsInfo.formId,
    formTitle: commentsInfo.formTitle,
    formVersion: commentsInfo.formVersionId,
    submissionCreateByUserId: commentsInfo.submissionCreateByUserId,
  };

  const formattedTime = (dateStr) => {
    const date = new Date(dateStr);
    const formattedTime = new Intl.DateTimeFormat('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    }).format(date);

    return formattedTime;
  };

  const getComments = async () => {
    setLoading(true);
    const result = await CallAPI(
      'GET_SUBMISSION_COMMENTS',
      {
        submissionId: commentsInfo.submissionId,
        ...(commentsInfo.elementGlobalId && { elementGlobalId: commentsInfo.elementGlobalId }),
        ...(commentsInfo.sectionRepetitionNumber && {
          sectionRepetitionNumber: Number(commentsInfo.sectionRepetitionNumber),
        }),
      },
      null,
    );
    if (result.status) {
      for (const comment of result.data.comments) {
        for (const media of comment.media) {
          const url = await getCommentImageUrl(media);
          if (url) {
            urlsRef.current[media._id] = url;
          }
        }
      }
      setComments(result.data.comments);

      const commentGroup = result.data.comments[0]?.commentGroup;
      if (
        commentGroup?.commentGroupUsers.find((u) => u.companyUserId === currentUserId)
          ?.isMessagesRead === false
      ) {
        const result = await CallAPI('UPDATE_SUBMISSION_COMMENT_READ_STATUS', {
          commentGroupId: commentGroup._id,
        });
        if (result.status) toast.success('Read status updated successfully');
        else toast.error(result.error);
      }
    }
    setLoading(false);
  };

  const getCommentImageUrl = async (media) => {
    const path = `${media.formId}/${media.versionId}/${media.customFolderId}/${media.imageName}`;
    let result = await CallAPI('GET_SUBMISSION_MEDIA_REDIRECT_URL', null, path, null, null);
    if (result.status) {
      return result.data.url;
    } else {
      return '';
    }
  };

  // this will show the latest comment by scrolling to the last comment
  useEffect(() => {
    const container = containerRef.current;
    if (container && comments.length) {
      container.scrollTop = container.scrollHeight; // go to the bottom end
    }
  }, [comments]);

  useEffect(() => {
    getComments();
  }, []);

  return (
    <AlertModal
      show={!!commentsInfo}
      actionButtons={[]}
      size='lg'
      noPadding
      backdropClassName={isNestedModal ? 'backdrop-overlap' : ''}
    >
      <div className='submission-comments-container'>
        <div className='submission-comments-modal-header'>
          <div className='d-flex gap-2 align-items-center'>
            <img className='msg-icon svg-black' src={messageSquareGreyIcon} />
            <label className='theme-size-1_3 theme-text-black-1'>{t('text_from_comments')}</label>
          </div>
          <img src={crossIcon} className='cursor-pointer' onClick={closeModal}></img>
        </div>

        <div className='submission-comments-modal-center'>
          <div ref={containerRef} className='submission-comments'>
            {loading && <Loader color='green-1' />}

            {!loading && commentsInfo.elementGlobalId && (
              <div className='theme-background-white-5 rounded-sm p-3'>
                <label className='theme-font-inter-medium text-black'>
                  {commentsInfo.elementTitle}
                </label>
              </div>
            )}

            {!comments.length && !loading && (
              <div className='h-100 d-flex flex-column align-items-center justify-content-center'>
                <div className='theme-font-inter-medium theme-text-black-1 theme-size-1_3'>
                  {t('text_comments')}
                </div>
                <div>{t('text_no_comment_description')}</div>
              </div>
            )}

            <div className='d-flex flex-column gap-3'>
              {comments.map((comment, index) => {
                return (
                  <div key={index} className='d-flex flex-column gap-2'>
                    <div className='d-flex flex-column gap-2'>
                      <div className='d-flex gap-2'>
                        <div className='theme-background-white-5 theme-width-48 theme-height-48 rounded-circle d-flex justify-content-center align-items-center text-uppercase text-white'>
                          <img
                            className='svg-grey'
                            src={
                              commentsInfo.submissionCreateByUserId === comment._id
                                ? userGreyIcon
                                : userCircleGreenIcon
                            }
                          />
                        </div>
                        <div>
                          <span className='text-black theme-size-1_2 theme-font-inter-medium'>
                            {`${comment.firstName} ${comment.lastName}`} &nbsp;
                          </span>
                          <div>{formattedTime(comment.createdAt)}</div>
                        </div>
                      </div>
                      <span className='theme-font-inter-regular text-black'>{comment.message}</span>
                    </div>

                    {comment.media?.length > 0 && (
                      <div className='d-flex flex-wrap gap-2'>
                        {comment.media.map(
                          (item) =>
                            item.mediaType === 'image' &&
                            urlsRef.current[item._id] && (
                              <div
                                key={item._id}
                                className='thumbnail-div theme-background-grey-15 theme-width-70 theme-height-70'
                              >
                                <img
                                  src={urlsRef.current[item._id]}
                                  className='imgThumbnail'
                                  onClick={() => window.open(urlsRef.current[item._id], '_blank')}
                                />
                              </div>
                            ),
                        )}

                        {(() => {
                          let docCounter = 0;
                          return comment.media.map((item) => {
                            const url = urlsRef.current[item._id];
                            if (item.mediaType !== 'file' || !url) return null;

                            docCounter += 1;
                            return (
                              <div
                                key={item._id}
                                className='thumbnail-div theme-background-grey-15 theme-width-70 theme-height-70'
                                onClick={() => window.open(url, '_blank')}
                              >
                                <img src={pdfIcon} alt='file' />
                                <span className='theme-text-green-1 truncate-string'>
                                  {docCounter}
                                </span>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className='submission-comments-modal-footer'>
          <CommentInputField
            addCommentPayload={addCommentPayload}
            getComments={getComments}
            mediaCustomFolderId={commentsInfo.mediaCustomFolderId}
          />
        </div>
      </div>
    </AlertModal>
  );
};

AddCommentModal.propTypes = {
  commentsInfo: PropTypes.object,
  closeModal: PropTypes.func.isRequired,
  currentUserId: PropTypes.string.isRequired,
  isNestedModal: PropTypes.bool,
};

const CommentInputField = ({ addCommentPayload, getComments, mediaCustomFolderId }) => {
  const { t } = useTranslation();
  const [newComment, setNewComment] = useState({ messageText: '', media: [] });
  const [fileType, setFileType] = useState({ type: '', triggerInput: false });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const maxCharacterLimit = 500;

  const attachmentOptionList = [
    { value: 'media', displayValue: 'select_photo', icon: imageIcon },
    { value: 'document', displayValue: 'select_document', icon: pdfGreenIcon },
  ];

  const handleTextChange = (value) => {
    let text = value;
    const isExceedingLimit = text.length > maxCharacterLimit;
    if (isExceedingLimit) {
      text = value.slice(0, maxCharacterLimit);
    }
    setErrors(isExceedingLimit ? { message: 'error max character limit' } : null);
    setNewComment((prevState) => ({ ...prevState, messageText: text }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewComment((prevState) => ({ ...prevState, media: [file] }));
    }
    e.target.value = null;
  };

  const handleUnAttachMedia = () => {
    setNewComment((prev) => ({ ...prev, media: [] }));
  };

  const clearNewComment = () => setNewComment({ messageText: '', media: [] });

  const handleMediaUploads = async () => {
    const mediaIds = [];
    for (const mediaFile of newComment.media) {
      const response = await CallAPI(
        'GET_SUBMISSION_MEDIA_UPLOAD_URL',
        {
          imageName: `${Date.now()}${mediaFile.name.slice(mediaFile.name.lastIndexOf('.'))}`,
          mediaType: mediaFile.type.includes('application/') ? 'file' : 'image',
          formId: addCommentPayload.form,
          versionId: addCommentPayload.formVersion,
          customFolderId: mediaCustomFolderId,
        },
        null,
        null,
      );
      if (response.status) {
        await uploadFile(mediaFile, response.data, null);
        mediaIds.push(response.data.id);
      }
    }
    return mediaIds;
  };

  const addComment = async () => {
    if (newComment.messageText !== '' || newComment.media.length > 0) {
      setLoading(true);
      try {
        const mediaResponses = await handleMediaUploads();
        const payload = {
          ...addCommentPayload,
          message: newComment.messageText,
          messageType: 'text',
          media: mediaResponses,
        };
        const result = await CallAPI('ADD_SUBMISSION_COMMENT', payload, null);
        if (result.status) {
          getComments();
        }
        clearNewComment();
      } catch (error) {
        console.log('addComment error: ', error);
      }
      setLoading(false);
    }
  };

  // adjust textarea height when overflow occur
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto'; // Reset the height before recalculating
      if (textarea.scrollHeight < 150) textarea.style.height = `${textarea.scrollHeight + 2}px`;
      // Set height based on scrollHeight
      else textarea.style.height = `150px`; // Set height based on scrollHeight
    }
  }, [newComment]);

  useEffect(() => {
    if (fileType.triggerInput && fileInputRef.current) {
      fileInputRef.current.click();
    }
    fileType.triggerInput = false;
  }, [fileType]);

  return (
    <div className='w-100'>
      {newComment.media.length > 0 && (
        <div className='attachmentContainer'>
          {newComment.media[0].type.includes('image/') ? (
            <div className='border border-secondary rounded '>
              <img
                src={URL.createObjectURL(newComment.media[0])}
                className='rounded imgThumbnail'
                alt='attachment'
              />
              <img
                className='ms-2 cursor-pointer'
                src={trashIcon}
                onClick={() => handleUnAttachMedia()}
                alt='Delete'
              />
            </div>
          ) : (
            <div className='d-inline-flex border border-secondary rounded p-2'>
              <img src={pdfIcon} alt='PDF icon' />
              <span className='ms-2 theme-text-green-1 truncate-string'>
                {newComment.media[0].name}
              </span>
              <img
                className='ms-3 cursor-pointer'
                src={trashIcon}
                onClick={() => handleUnAttachMedia()}
                alt='Delete'
              />
            </div>
          )}
        </div>
      )}

      {errors?.message && (
        <label className='theme-text-red-1 w-100'>
          {t(errors.message)}
          {` (${maxCharacterLimit})`}
        </label>
      )}

      <div className='d-flex gap-2 align-items-end'>
        <input
          className='d-none'
          type='file'
          accept={fileType.type === 'media' ? '.jpg, .jpeg, .png' : 'application/pdf'}
          ref={fileInputRef}
          onChange={handleFileChange}
        />
        <NestedSelect
          options={attachmentOptionList}
          name='media'
          drop='up'
          handleChange={(_, value) => setFileType({ type: value, triggerInput: true })}
          toggleComponent={
            <div className='icon-div border-grey-1-h-1'>
              <img
                src={plusIcon}
                className='input-icon'
                onClick={() => (!loading ? addComment() : null)}
              />
            </div>
          }
          toggleClasses={`${newComment.media.length > 0 ? 'pe-none opacity-50' : ''}`} //disable click when media attached
        />

        <div className='custom-input-box w-100'>
          <div className='custom-input-field'>
            <textarea
              ref={textareaRef}
              rows={1}
              type='text'
              style={{ resize: 'none', padding: '0.6rem 0.75rem' }}
              name={'message'}
              placeholder={t('text_type_here')}
              value={newComment.messageText}
              autoComplete='off'
              onChange={(e) => handleTextChange(e.target.value)}
            />
          </div>
        </div>

        <div
          className='icon-div theme-background-green-1'
          onClick={() => (!loading ? addComment() : null)}
        >
          <img src={sendIcon} className='svg-white' />
        </div>
      </div>
    </div>
  );
};

CommentInputField.propTypes = {
  addCommentPayload: PropTypes.object.isRequired,
  mediaCustomFolderId: PropTypes.string.isRequired,
  getComments: PropTypes.func.isRequired,
};

export default AddCommentModal;
